import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, MailQuestion } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.error(typeof msg === "string" ? msg : "Too many attempts — please wait a while.");
      } else {
        // Anti-enumeration: still show the generic success message.
        setSent(true);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">My RKF</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">Password recovery</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            Locked out?<br/>We'll get you back in<span className="text-blue-400">.</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-md text-sm leading-relaxed">
            Enter the email address on your account. If it's registered, we'll send a one-time link valid for 30 minutes.
          </p>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">v1.0 · Secure by design</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8" data-testid="forgot-password-card">
            {sent ? (
              <div className="text-center space-y-4" data-testid="forgot-password-success">
                <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
                <h2 className="font-display text-2xl font-bold">Check your inbox</h2>
                <p className="text-sm text-slate-600">
                  If this email is registered, a reset link has been sent. The link works once and expires in 30 minutes.
                </p>
                <p className="text-xs text-slate-500">
                  Didn't get anything? Check your spam folder, or try again in a few minutes.
                </p>
                <Link to="/login" className="inline-block text-sm text-blue-600 hover:underline">Back to sign in</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <MailQuestion className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-bold">Forgot password?</h2>
                    <p className="text-sm text-slate-500 mt-1">
                      We'll send you a link to set a new one.
                    </p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="forgot-password-email"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0F172A] hover:bg-slate-800" data-testid="forgot-password-submit">
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Remembered it? <Link to="/login" className="text-blue-600 font-semibold">Sign in</Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
